import React, { useEffect, useState, type KeyboardEvent } from 'react';
import { CloseIcon, LockIcon, PlusIcon } from '../../../assets/icons/icons';
import { Button } from '../../ui/Button/Button';
import { IconButton } from '../../ui/IconButton/IconButton';
import { Modal, ModalActions, ModalNote } from '../../ui/Modal/Modal';
import {
    useBlockOrgHostMutation,
    useGetOrgBlockedHostsQuery,
    useUnblockOrgHostMutation,
} from '../../../entities/blocks/api/blocksApi';
import styles from './StopListModal.module.scss';

interface StopListModalProps {
    open: boolean;
    onClose: () => void;
    onNotify: (message: string) => void;
    organizationId?: string | number;
}

function toUrl(input: string): string | null {
    const value = input.trim();
    if (!value) return null;
    const withProtocol = /^https?:\/\//i.test(value) ? value : `https://${value}`;
    try {
        const url = new URL(withProtocol);
        return url.hostname.includes('.') ? withProtocol : null;
    } catch {
        return null;
    }
}

function getHost(item: string): string {
    try {
        return new URL(item).host;
    } catch {
        return item;
    }
}

export function StopListModal({ open, onClose, onNotify, organizationId }: StopListModalProps) {
    const [host, setHost] = useState('');
    const [search, setSearch] = useState('');

    useEffect(() => {
        if (open) {
            setHost('');
            setSearch('');
        }
    }, [open]);

    const { data, isLoading, isError } = useGetOrgBlockedHostsQuery(organizationId!, {
        skip: !open || !organizationId,
    });

    const [blockHost, { isLoading: isAdding }] = useBlockOrgHostMutation();
    const [unblockHost, { isLoading: isRemoving }] = useUnblockOrgHostMutation();

    const hosts = data ?? [];
    const filtered = hosts.filter((item) => item.includes(search.trim()));
    const busy = isAdding || isRemoving;

    const addEntry = async () => {
        if (busy || !organizationId) return;

        const value = toUrl(host);
        if (!value) {
            onNotify('Введите корректный URL');
            return;
        }

        if (hosts.some((item) => getHost(item) === getHost(value))) {
            onNotify('Этот источник уже заблокирован');
            return;
        }

        try {
            await blockHost({ orgId: organizationId, host: value }).unwrap();
            setHost('');
            onNotify(`Источник ${value} добавлен в заблокированные`);
        } catch (error) {
            const err = error as { data?: { message?: string } };
            if (err.data?.message === 'hostname already exists') {
                onNotify('Этот источник уже заблокирован');
            } else {
                onNotify('Произошла ошибка при блокировке источника');
            }
        }
    };

    const removeEntry = async (item: string) => {
        if (busy || !organizationId) return;

        try {
            await unblockHost({ orgId: organizationId, host: item }).unwrap();
            onNotify('Источник удалён из списка заблокированных');
        } catch {
            onNotify('Произошла ошибка при удалении источника');
        }
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') void addEntry();
    };

    return (
        <Modal
            open={open}
            title="Стоп-лист сайтов"
            description="Предложения с этих сайтов не попадают в результаты анализа цен."
            icon={<LockIcon />}
            onClose={onClose}
            footer={
                <>
                    <ModalNote>
                        Всего источников: <b>{hosts.length}</b>
                    </ModalNote>
                    <ModalActions>
                        <Button onClick={onClose}>Закрыть</Button>
                    </ModalActions>
                </>
            }
        >
            <div className={styles.addSource}>
                <input
                    value={host}
                    onChange={(event) => setHost(event.target.value)}
                    onKeyDown={onKeyDown}
                    placeholder="Например, https://example-shop.ru"
                    autoComplete="off"
                    aria-label="Ссылка на источник"
                    disabled={busy}
                />
                <Button icon={<PlusIcon />} disabled={busy || !host.trim()} onClick={() => void addEntry()}>
                    {isAdding ? 'Добавляем…' : 'Добавить'}
                </Button>
            </div>

            {hosts.length > 0 && (
                <div className={styles.addSource}>
                    <input
                        value={search}
                        onChange={(event) => setSearch(event.target.value)}
                        placeholder="Найти в стоп-листе"
                        autoComplete="off"
                        aria-label="Поиск в стоп-листе"
                    />
                </div>
            )}

            {isError ? (
                <p className={styles.emptyNote}>Произошла ошибка при получении списка заблокированных источников</p>
            ) : isLoading ? (
                <p className={styles.emptyNote}>Загружаем стоп-лист…</p>
            ) : hosts.length === 0 ? (
                <p className={styles.emptyNote}>На текущий момент заблокированных источников нет</p>
            ) : (
                <div className={styles.list}>
                    {filtered.map((item) => {
                        const domain = getHost(item);
                        return (
                            <div key={item} className={styles.item}>
                                <div className={styles.fav}>{domain[0]}</div>
                                <div>
                                    <a href={item} target="_blank" rel="noreferrer">
                                        <b>{domain}</b>
                                    </a>
                                </div>
                                <IconButton
                                    tone="danger"
                                    className={styles.remove}
                                    disabled={busy}
                                    onClick={() => void removeEntry(item)}
                                >
                                    <CloseIcon />
                                </IconButton>
                            </div>
                        );
                    })}
                </div>
            )}

            <p className={styles.note}>
                Блокировка действует на все проекты организации. Снятая блокировка применяется при
                следующем запуске поиска.
            </p>
        </Modal>
    );
}